import React, { useEffect, useRef, useState } from 'react';
import { useDesign } from '../../designs';
import { loadMapsApi } from './PlacesAutocomplete';

interface SinglePinMapProps {
  lat?: number;
  lng?: number;
  address?: string;
  label?: string;
  height?: number;
}

/** Dark map style matching MapView — keeps the Parlor UI consistent */
const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#1d1d2e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a2e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#6a6a8a' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#2a2a40' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a2a42' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1f1f35' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#353550' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#111122' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4a4a6a' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1a2a1a' }, { visibility: 'on' }] },
];

/**
 * Single-pin dark map — matches MapView styling, uses the Maps JavaScript API
 * (not the Embed API). Accepts lat/lng directly, or an address to geocode.
 */
export default function SinglePinMap({ lat, lng, address, label, height = 200 }: SinglePinMapProps) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState('');
  const [resolved, setResolved] = useState<{ lat: number; lng: number } | null>(
    lat != null && lng != null ? { lat, lng } : null
  );

  const hasQuery = resolved != null || !!address;

  // Load the Maps API
  useEffect(() => {
    if (!hasQuery || !mapRef.current) return;
    const w = window as any;
    if (w.google?.maps) { setMapReady(true); return; }
    loadMapsApi()
      .then(() => { setMapReady(true); setMapError(''); })
      .catch(() => { setMapError('Map unavailable'); });
  }, [hasQuery]);

  // Geocode address if we don't have coords
  useEffect(() => {
    if (resolved || !mapReady || !address) return;
    const w = window as any;
    const google = w.google;
    if (!google?.maps?.Geocoder) return;
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address }, (results: any, status: string) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        const loc = results[0].geometry.location;
        setResolved({ lat: loc.lat(), lng: loc.lng() });
      } else {
        setMapError('Could not find location');
      }
    });
  }, [mapReady, address, resolved]);

  // Render map + single marker
  useEffect(() => {
    if (!mapReady || !mapRef.current || !resolved) return;
    const w = window as any;
    const google = w.google;
    if (!google?.maps) return;
    try {
      const map = new google.maps.Map(mapRef.current, {
        center: { lat: resolved.lat, lng: resolved.lng },
        zoom: 15,
        disableDefaultUI: true,
        zoomControl: true,
        styles: DARK_MAP_STYLES,
        backgroundColor: '#1d1d2e',
      });
      mapInstanceRef.current = map;

      if (markerRef.current) markerRef.current.setMap(null);
      markerRef.current = new google.maps.Marker({
        position: { lat: resolved.lat, lng: resolved.lng },
        map,
        label: label
          ? { text: label, color: '#FFFFFF', fontSize: '10px', fontWeight: 'bold' }
          : undefined,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 14,
          fillColor: d.accent,
          fillOpacity: 1,
          strokeColor: 'rgba(255,255,255,0.25)',
          strokeWeight: 1.5,
        },
      });
    } catch (err) {
      console.error('Failed to render map:', err);
      setMapError('Map unavailable');
    }
  }, [mapReady, resolved, label, d.accent]);

  if (!hasQuery) return null;

  if (mapError) {
    return (
      <div style={{
        width: '100%', height: `${height}px`,
        backgroundColor: d.bgSurface,
        border: `1px solid ${d.border}`, borderRadius: '2px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
          color: d.textSecondary,
        }}>{mapError}</span>
      </div>
    );
  }

  return (
    <div ref={mapRef} style={{
      width: '100%', height: `${height}px`,
      border: `1px solid ${d.border}`, borderRadius: '2px',
      backgroundColor: d.bgSurface,
    }} />
  );
}
